import React from 'react';
import { useTranslation } from 'react-i18next';
import PhoneVerification from '../PhoneVerification';

export interface PersonalInfoSectionProps {
  name: string;
  nickname: string;
  phoneNumber: string;
  phoneVerified: boolean;
  onChange: (field: string, value: string | boolean) => void;
  errors: Record<string, string>;
  isPhoneExisting?: boolean;
  hidePhoneVerification?: boolean;
}

/**
 * 개인정보 section — MatchGo "Bright" styling.
 * Row layout: a 120px label column + field column (collapses to one column
 * on mobile). Section header uses the navy underline accent.
 */
const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  name,
  nickname,
  phoneNumber,
  phoneVerified,
  onChange,
  errors,
  isPhoneExisting = false,
  hidePhoneVerification = false,
}) => {
  const { t } = useTranslation('profile');

  return (
    <section className="px-6 sm:px-8 py-7">
      <div className="flex items-center justify-between gap-2 pb-3.5 mb-2 border-b-2 border-[#0F1E3D]">
        <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">
          {t('myProfile.form.personalInfo')}
        </h2>
        <span className="font-mono text-[10px] font-semibold text-[#6B7794] bg-[#F7F8FB] border border-[#E4E8F0] rounded px-2 py-0.5">
          {t('myProfile.form.savedTag', '저장 버튼 대상')}
        </span>
      </div>

      {/* Name (required) */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5 flex items-center gap-1">
          {t('myProfile.form.name')} <span className="text-[#FF6B6B]">*</span>
        </div>
        <div>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder={t('myProfile.form.namePlaceholder')}
            className={`w-full max-w-[320px] px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] bg-white text-[#0F1E3D] transition focus:outline-none focus:ring-2 ${
              errors.name
                ? 'border-[#F25151] focus:border-[#F25151] focus:ring-[#FFE4E4]'
                : 'border-[#E4E8F0] focus:border-[#2D5BFF] focus:ring-[#E6EDFF]'
            }`}
          />
          {errors.name && <p className="mt-1.5 text-xs text-[#F25151]">{errors.name}</p>}
        </div>
      </div>

      {/* Nickname (optional) */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5">
          {t('myProfile.form.nickname')}
        </div>
        <div>
          <input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => onChange('nickname', e.target.value)}
            placeholder={t('myProfile.form.nicknamePlaceholder')}
            className="w-full max-w-[320px] px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] border-[#E4E8F0] bg-white text-[#0F1E3D] transition focus:outline-none focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]"
          />
        </div>
      </div>

      {/* Phone Number + Phone Verification */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5 flex items-center gap-1">
          {t('myProfile.form.phoneNumber')}
          <span className="relative group inline-flex">
            <span className="cursor-help font-mono text-[10px] w-[15px] h-[15px] rounded-full border-[1.4px] border-[#A0AAC2] text-[#6B7794] flex items-center justify-center">i</span>
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs font-normal text-white bg-[#0F1E3D] rounded-lg shadow-lg z-10 leading-relaxed">
              {t('myProfile.form.phoneTooltip')}
            </span>
          </span>
        </div>
        <div>
          <PhoneVerification
            phoneNumber={phoneNumber}
            isVerified={phoneVerified}
            onVerified={() => onChange('phoneVerified', true)}
            onPhoneChange={(phone) => onChange('phoneNumber', phone)}
            isExisting={isPhoneExisting}
            hideVerifyButton={hidePhoneVerification}
          />
          {errors.phoneNumber && <p className="mt-1.5 text-xs text-[#F25151]">{errors.phoneNumber}</p>}
        </div>
      </div>
    </section>
  );
};

export default PersonalInfoSection;
